import { useState, useEffect, useCallback } from 'react';
import api from '../../api';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Wrench, Search, ExternalLink, Plus, Edit, Trash2, RefreshCw,
  ChevronLeft, ChevronRight, ChevronUp, ChevronDown, ChevronsUpDown,
  AlertCircle, CheckCircle, Clock, Package, Download, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const KBCatalogPage = () => {
  const [activeTab, setActiveTab] = useState('catalog');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cardFilter, setCardFilter] = useState(''); // '', 'con_info', 'sin_datos'
  
  // Catalog tab state
  const [catalog, setCatalog] = useState({ items: [], total: 0, page: 1, pages: 0 });
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogTipo, setCatalogTipo] = useState('');
  const [catalogSort, setCatalogSort] = useState({ field: 'fecha_consulta', dir: 'desc' });
  
  // Pending tab state
  const [pending, setPending] = useState({ items: [], total: 0, page: 1, pages: 0 });
  const [pendingPage, setPendingPage] = useState(1);
  const [pendingSort, setPendingSort] = useState({ field: 'apariciones', dir: 'desc' });
  
  // Modals
  const [showLookupModal, setShowLookupModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [lookupKbId, setLookupKbId] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [editingKb, setEditingKb] = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Batch lookup
  const [batchLoading, setBatchLoading] = useState(false);
  const [selectedPending, setSelectedPending] = useState([]);
  
  // Catalog selection for re-lookup
  const [selectedCatalog, setSelectedCatalog] = useState([]);
  const [relookupLoading, setRelookupLoading] = useState(false);

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await api.get('/config/kb-catalog/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Fetch catalog
  const fetchCatalog = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: catalog.page,
        page_size: 25,
        sort_by: catalogSort.field,
        sort_dir: catalogSort.dir,
      });
      if (catalogSearch) params.append('search', catalogSearch);
      if (catalogTipo) params.append('tipo', catalogTipo);
      if (cardFilter === 'con_info') params.append('sin_datos', 'false');
      if (cardFilter === 'sin_datos') params.append('sin_datos', 'true');
      
      const response = await api.get(`/config/kb-catalog?${params}`);
      setCatalog(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      toast.error('Error al cargar catálogo');
    } finally {
      setLoading(false);
    }
  }, [catalog.page, catalogSearch, catalogTipo, catalogSort, cardFilter]);

  // Fetch pending
  const fetchPending = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: pendingPage,
        page_size: 50,
        sort_by: pendingSort.field,
        sort_dir: pendingSort.dir,
      });
      const response = await api.get(`/config/kb-catalog/pending?${params}`);
      setPending(response.data);
    } catch (error) {
      toast.error('Error al cargar KBs pendientes');
    }
  }, [pendingPage, pendingSort]);

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    if (activeTab === 'catalog') fetchCatalog();
    if (activeTab === 'pending') fetchPending();
  }, [activeTab, fetchCatalog, fetchPending]);

  // Sort handler for catalog
  const handleSort = (field) => {
    setCatalogSort(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
    setCatalog(prev => ({ ...prev, page: 1 }));
  };

  // Sort handler for pending
  const handlePendingSort = (field) => {
    setPendingSort(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
    setPendingPage(1);
  };

  const SortIcon = ({ field, sortState }) => {
    if (sortState.field !== field) {
      return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
    }
    return sortState.dir === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-primary" />
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };

  // Lookup single KB
  const handleLookup = async () => {
    if (!lookupKbId.trim()) {
      toast.error('Ingrese un KB ID');
      return;
    }
    
    setLookupLoading(true);
    try {
      const response = await api.post('/config/kb-catalog/lookup', { kb_id: lookupKbId });
      
      if (response.data.success) {
        toast.success(`KB ${response.data.kb_id} agregado al catálogo`);
        setShowLookupModal(false);
        setLookupKbId('');
        fetchStats();
        fetchCatalog();
      } else if (response.data.sin_datos) {
        toast.info(`KB ${response.data.kb_id} registrado pero sin información disponible`);
        setShowLookupModal(false);
        setLookupKbId('');
        fetchStats();
        fetchCatalog();
      } else {
        toast.error(response.data.message || 'Error al buscar KB');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al buscar KB');
    } finally {
      setLookupLoading(false);
    }
  };

  // Batch lookup (for pending)
  const handleBatchLookup = async () => {
    if (selectedPending.length === 0) {
      toast.error('Seleccione al menos un KB');
      return;
    }
    
    setBatchLoading(true);
    try {
      const response = await api.post('/config/kb-catalog/batch-lookup', { kb_ids: selectedPending });
      const { success_count, no_data_count, total_processed } = response.data;
      toast.success(`Procesados: ${total_processed} | Con info: ${success_count} | Sin datos: ${no_data_count}`);
      setSelectedPending([]);
      fetchStats();
      fetchPending();
    } catch (error) {
      toast.error('Error en procesamiento batch');
    } finally {
      setBatchLoading(false);
    }
  };

  // Re-lookup for catalog items
  const handleRelookup = async (kbIds) => {
    const ids = Array.isArray(kbIds) ? kbIds : [kbIds];
    if (ids.length === 0) return;
    
    setRelookupLoading(true);
    try {
      const response = await api.post('/config/kb-catalog/batch-lookup', { kb_ids: ids });
      const { success_count, no_data_count, total_processed } = response.data;
      toast.success(`Re-buscados: ${total_processed} | Actualizados: ${success_count} | Sin datos: ${no_data_count}`);
      setSelectedCatalog([]);
      fetchStats();
      fetchCatalog();
    } catch (error) {
      toast.error('Error al re-buscar');
    } finally {
      setRelookupLoading(false);
    }
  };

  // Toggle catalog selection
  const toggleCatalogSelect = (kbId) => {
    setSelectedCatalog(prev => 
      prev.includes(kbId) 
        ? prev.filter(id => id !== kbId)
        : [...prev, kbId]
    );
  };

  const toggleCatalogSelectAll = () => {
    if (selectedCatalog.length === catalog.items.length) {
      setSelectedCatalog([]);
    } else {
      setSelectedCatalog(catalog.items.map(k => k.kb_id));
    }
  };

  // Edit KB
  const handleEdit = (kb) => {
    setEditingKb(kb);
    setFormData({
      titulo: kb.titulo || '',
      tipo: kb.tipo || '',
      productos: kb.productos || '',
      descripcion: kb.descripcion || '',
      fecha_release: kb.fecha_release || '',
      tamano: kb.tamano || '',
    });
    setShowEditModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/config/kb-catalog/${editingKb.kb_id}`, formData);
      toast.success('KB actualizado');
      setShowEditModal(false);
      fetchStats();
      fetchCatalog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // Toggle pending selection
  const togglePendingSelect = (kbId) => {
    setSelectedPending(prev => 
      prev.includes(kbId) 
        ? prev.filter(id => id !== kbId)
        : [...prev, kbId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedPending.length === pending.items.length) {
      setSelectedPending([]);
    } else {
      setSelectedPending(pending.items.map(p => p.kb_id));
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title flex items-center gap-3">
              <Wrench className="h-6 w-6 text-primary" />
              Catálogo de KBs
            </h1>
            <p className="page-subtitle">Gestión del catálogo de actualizaciones Microsoft</p>
          </div>
          <Button onClick={() => setShowLookupModal(true)} className="gap-2" data-testid="add-kb-btn">
            <Plus className="h-4 w-4" />
            Buscar KB
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card 
              className="nerve-card cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => { setCardFilter(''); setActiveTab('catalog'); setCatalog(prev => ({ ...prev, page: 1 })); }}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Package className="h-8 w-8 text-primary" />
                  <div>
                    <p className="text-2xl font-bold text-primary">{stats.total_en_parches}</p>
                    <p className="text-xs text-muted-foreground">KBs en Parches</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card 
              className="nerve-card cursor-pointer hover:border-green-500/50 transition-colors"
              onClick={() => { setCardFilter('con_info'); setActiveTab('catalog'); setCatalog(prev => ({ ...prev, page: 1 })); }}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-8 w-8 text-green-500" />
                  <div>
                    <p className="text-2xl font-bold text-green-500">{stats.con_info}</p>
                    <p className="text-xs text-muted-foreground">Con Información</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card 
              className="nerve-card cursor-pointer hover:border-gray-500/50 transition-colors"
              onClick={() => { setCardFilter('sin_datos'); setActiveTab('catalog'); setCatalog(prev => ({ ...prev, page: 1 })); }}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <AlertCircle className="h-8 w-8 text-gray-500" />
                  <div>
                    <p className="text-2xl font-bold text-gray-500">{stats.sin_datos || 0}</p>
                    <p className="text-xs text-muted-foreground">Sin Datos</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card 
              className="nerve-card cursor-pointer hover:border-yellow-500/50 transition-colors"
              onClick={() => { setActiveTab('pending'); }}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Clock className="h-8 w-8 text-yellow-500" />
                  <div>
                    <p className="text-2xl font-bold text-yellow-500">{stats.pendientes}</p>
                    <p className="text-xs text-muted-foreground">Pendientes</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="nerve-card p-1">
            <TabsTrigger value="catalog" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-md">
              Catálogo ({stats?.total_catalogados || 0})
            </TabsTrigger>
            <TabsTrigger value="pending" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-md">
              Pendientes ({stats?.pendientes || 0})
            </TabsTrigger>
          </TabsList>

          {/* Catalog Tab */}
          <TabsContent value="catalog">
            <Card className="nerve-card">
              <CardHeader className="border-b border-border pb-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <CardTitle className="text-sm font-semibold">KBs Catalogados</CardTitle>
                  <div className="flex items-center gap-2">
                    {selectedCatalog.length > 0 && (
                      <>
                        <span className="text-sm text-muted-foreground">{selectedCatalog.length} seleccionados</span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRelookup(selectedCatalog)}
                          disabled={relookupLoading}
                          className="gap-1"
                        >
                          {relookupLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                          Re-buscar
                        </Button>
                      </>
                    )}
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Buscar KB..."
                        value={catalogSearch}
                        onChange={(e) => { setCatalogSearch(e.target.value); setCatalog(prev => ({ ...prev, page: 1 })); }}
                        className="pl-8 h-8 w-48"
                      />
                    </div>
                    <Select value={catalogTipo} onValueChange={(v) => { setCatalogTipo(v === 'all' ? '' : v); setCatalog(prev => ({ ...prev, page: 1 })); }}>
                      <SelectTrigger className="h-8 w-40">
                        <SelectValue placeholder="Todos los tipos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos los tipos</SelectItem>
                        <SelectItem value="Security Update">Security Update</SelectItem>
                        <SelectItem value="Cumulative Update">Cumulative Update</SelectItem>
                        <SelectItem value="Preview Update">Preview Update</SelectItem>
                        <SelectItem value=".NET Update">.NET Update</SelectItem>
                        <SelectItem value="Update">Update</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : catalog.items.length > 0 ? (
                  <>
                    <table className="table-subtle">
                      <thead>
                        <tr>
                          <th className="w-10">
                            <input
                              type="checkbox"
                              checked={selectedCatalog.length === catalog.items.length && catalog.items.length > 0}
                              onChange={toggleCatalogSelectAll}
                              className="rounded border-border"
                            />
                          </th>
                          <th 
                            className="cursor-pointer hover:bg-muted/50 select-none"
                            onClick={() => handleSort('kb_id')}
                          >
                            <div className="flex items-center gap-1">
                              KB ID
                              <SortIcon field="kb_id" sortState={catalogSort} />
                            </div>
                          </th>
                          <th 
                            className="cursor-pointer hover:bg-muted/50 select-none"
                            onClick={() => handleSort('titulo')}
                          >
                            <div className="flex items-center gap-1">
                              TITULO
                              <SortIcon field="titulo" sortState={catalogSort} />
                            </div>
                          </th>
                          <th 
                            className="cursor-pointer hover:bg-muted/50 select-none"
                            onClick={() => handleSort('tipo')}
                          >
                            <div className="flex items-center gap-1">
                              TIPO
                              <SortIcon field="tipo" sortState={catalogSort} />
                            </div>
                          </th>
                          <th>PRODUCTOS</th>
                          <th 
                            className="cursor-pointer hover:bg-muted/50 select-none"
                            onClick={() => handleSort('fecha_release')}
                          >
                            <div className="flex items-center gap-1">
                              FECHA
                              <SortIcon field="fecha_release" sortState={catalogSort} />
                            </div>
                          </th>
                          <th className="text-right">ACCIONES</th>
                        </tr>
                      </thead>
                      <tbody>
                        {catalog.items.map((kb) => (
                          <tr key={kb.kb_id}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedCatalog.includes(kb.kb_id)}
                                onChange={() => toggleCatalogSelect(kb.kb_id)}
                                className="rounded border-border"
                              />
                            </td>
                            <td>
                              <span className="font-mono text-primary font-medium">{kb.kb_id}</span>
                            </td>
                            <td className="max-w-[300px]">
                              {kb.sin_datos ? (
                                <span className="text-muted-foreground italic">Sin información disponible</span>
                              ) : kb.titulo ? (
                                <span className="truncate block" title={kb.titulo}>{kb.titulo}</span>
                              ) : (
                                <span className="text-muted-foreground italic">-</span>
                              )}
                            </td>
                            <td>
                              {kb.sin_datos ? (
                                <span className="badge bg-gray-500/20 text-gray-400">Sin datos</span>
                              ) : kb.tipo ? (
                                <span className={`badge ${
                                  kb.tipo.includes('Security') ? 'badge-error' :
                                  kb.tipo.includes('Cumulative') ? 'badge-warning' :
                                  kb.tipo.includes('Preview') ? 'badge-info' :
                                  'badge-default'
                                }`}>
                                  {kb.tipo}
                                </span>
                              ) : '-'}
                            </td>
                            <td className="text-sm text-muted-foreground max-w-[150px] truncate" title={kb.productos}>
                              {kb.productos || '-'}
                            </td>
                            <td className="text-sm text-muted-foreground">
                              {kb.fecha_release || '-'}
                            </td>
                            <td>
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => handleRelookup(kb.kb_id)}
                                  title="Re-buscar"
                                  disabled={relookupLoading}
                                >
                                  <RefreshCw className={`h-3.5 w-3.5 ${relookupLoading ? 'animate-spin' : ''}`} />
                                </Button>
                                {kb.url_catalogo && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={() => window.open(kb.url_catalogo, '_blank')}
                                    title="Abrir en Microsoft Catalog"
                                  >
                                    <ExternalLink className="h-3.5 w-3.5" />
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => handleEdit(kb)}
                                  title="Editar"
                                >
                                  <Edit className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {catalog.pages > 1 && (
                      <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                        <span className="text-sm text-muted-foreground">
                          {(catalog.page - 1) * 25 + 1} - {Math.min(catalog.page * 25, catalog.total)} de {catalog.total}
                        </span>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline" size="sm"
                            onClick={() => setCatalog(prev => ({ ...prev, page: prev.page - 1 }))}
                            disabled={catalog.page === 1}
                            className="h-8 w-8 p-0"
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <span className="text-sm">{catalog.page} / {catalog.pages}</span>
                          <Button
                            variant="outline" size="sm"
                            onClick={() => setCatalog(prev => ({ ...prev, page: prev.page + 1 }))}
                            disabled={catalog.page === catalog.pages}
                            className="h-8 w-8 p-0"
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-12">
                    <Wrench className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">
                      {catalogSearch ? 'Sin resultados para la búsqueda' : 'No hay KBs en el catálogo'}
                    </p>
                    <Button variant="link" onClick={() => setShowLookupModal(true)} className="mt-2">
                      Agregar primer KB
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pending Tab */}
          <TabsContent value="pending">
            <Card className="nerve-card">
              <CardHeader className="border-b border-border pb-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <CardTitle className="text-sm font-semibold">
                    KBs detectados sin información ({pending.total})
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    {selectedPending.length > 0 && (
                      <span className="text-sm text-muted-foreground">
                        {selectedPending.length} seleccionados
                      </span>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleBatchLookup}
                      disabled={selectedPending.length === 0 || batchLoading}
                      className="gap-2"
                    >
                      {batchLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4" />
                      )}
                      Buscar Seleccionados ({selectedPending.length})
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {pending.items.length > 0 ? (
                  <>
                    <table className="table-subtle">
                      <thead>
                        <tr>
                          <th className="w-10">
                            <input
                              type="checkbox"
                              checked={selectedPending.length === pending.items.length && pending.items.length > 0}
                              onChange={toggleSelectAll}
                              className="rounded border-border"
                            />
                          </th>
                          <th 
                            className="cursor-pointer hover:bg-muted/50 select-none"
                            onClick={() => handlePendingSort('kb_id')}
                          >
                            <div className="flex items-center gap-1">
                              KB ID
                              <SortIcon field="kb_id" sortState={pendingSort} />
                            </div>
                          </th>
                          <th 
                            className="cursor-pointer hover:bg-muted/50 select-none"
                            onClick={() => handlePendingSort('apariciones')}
                          >
                            <div className="flex items-center gap-1">
                              APARICIONES
                              <SortIcon field="apariciones" sortState={pendingSort} />
                            </div>
                          </th>
                          <th 
                            className="cursor-pointer hover:bg-muted/50 select-none"
                            onClick={() => handlePendingSort('ejemplo_fuente')}
                          >
                            <div className="flex items-center gap-1">
                              FUENTE
                              <SortIcon field="ejemplo_fuente" sortState={pendingSort} />
                            </div>
                          </th>
                          <th>EJEMPLO EQUIPO</th>
                          <th className="text-right">ACCION</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pending.items.map((item) => (
                          <tr key={item.kb_id}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedPending.includes(item.kb_id)}
                                onChange={() => togglePendingSelect(item.kb_id)}
                                className="rounded border-border"
                              />
                            </td>
                            <td>
                              <span className="font-mono text-primary font-medium">{item.kb_id}</span>
                            </td>
                            <td>
                              <span className="badge badge-info">{item.apariciones}</span>
                            </td>
                            <td className="text-muted-foreground">{item.ejemplo_fuente}</td>
                            <td className="text-muted-foreground text-sm">{item.ejemplo_equipo}</td>
                            <td>
                              <div className="flex justify-end">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setLookupKbId(item.kb_id);
                                    setShowLookupModal(true);
                                  }}
                                  className="gap-1"
                                >
                                  <Search className="h-3.5 w-3.5" />
                                  Buscar
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {pending.pages > 1 && (
                      <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                        <span className="text-sm text-muted-foreground">
                          Página {pending.page} de {pending.pages}
                        </span>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline" size="sm"
                            onClick={() => setPendingPage(p => p - 1)}
                            disabled={pendingPage === 1}
                            className="h-8 w-8 p-0"
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline" size="sm"
                            onClick={() => setPendingPage(p => p + 1)}
                            disabled={pendingPage === pending.pages}
                            className="h-8 w-8 p-0"
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-12">
                    <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                    <p className="text-muted-foreground">Todos los KBs tienen información</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Lookup Modal */}
        <Dialog open={showLookupModal} onOpenChange={setShowLookupModal}>
          <DialogContent className="modal-content">
            <DialogHeader className="modal-header">
              <DialogTitle className="flex items-center gap-2">
                <Search className="h-5 w-5 text-primary" />
                Buscar KB en Microsoft Catalog
              </DialogTitle>
            </DialogHeader>
            <div className="modal-body py-4 space-y-4">
              <div>
                <Label>KB ID</Label>
                <Input
                  value={lookupKbId}
                  onChange={(e) => setLookupKbId(e.target.value)}
                  placeholder="Ej: KB5034441"
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Ingrese el ID del KB (con o sin el prefijo "KB")
                </p>
              </div>
              <div className="p-3 bg-muted/50 rounded-md">
                <p className="text-sm text-muted-foreground">
                  <AlertCircle className="h-4 w-4 inline mr-1" />
                  El Microsoft Catalog puede requerir entrada manual de datos debido a restricciones de JavaScript.
                  Se creará un registro con el link al catálogo para completar la información.
                </p>
              </div>
            </div>
            <DialogFooter className="modal-footer">
              <Button variant="outline" onClick={() => setShowLookupModal(false)}>Cancelar</Button>
              <Button onClick={handleLookup} disabled={lookupLoading} className="gap-2">
                {lookupLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                Buscar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="modal-content max-w-lg">
            <DialogHeader className="modal-header">
              <DialogTitle className="flex items-center gap-2">
                <Edit className="h-5 w-5 text-primary" />
                Editar KB - {editingKb?.kb_id}
              </DialogTitle>
            </DialogHeader>
            <div className="modal-body py-4 space-y-4">
              {editingKb?.url_catalogo && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(editingKb.url_catalogo, '_blank')}
                  className="w-full gap-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  Abrir en Microsoft Catalog (para copiar datos)
                </Button>
              )}
              <div>
                <Label>Título</Label>
                <Input
                  value={formData.titulo || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, titulo: e.target.value }))}
                  placeholder="Ej: 2024-01 Cumulative Update for Windows 11..."
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tipo</Label>
                  <Select value={formData.tipo || ''} onValueChange={(v) => setFormData(prev => ({ ...prev, tipo: v }))}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Seleccione tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Security Update">Security Update</SelectItem>
                      <SelectItem value="Cumulative Update">Cumulative Update</SelectItem>
                      <SelectItem value="Preview Update">Preview Update</SelectItem>
                      <SelectItem value=".NET Update">.NET Update</SelectItem>
                      <SelectItem value="Driver Update">Driver Update</SelectItem>
                      <SelectItem value="Definition Update">Definition Update</SelectItem>
                      <SelectItem value="Update">Update</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Fecha Release</Label>
                  <Input
                    type="date"
                    value={formData.fecha_release || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, fecha_release: e.target.value }))}
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label>Productos</Label>
                <Input
                  value={formData.productos || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, productos: e.target.value }))}
                  placeholder="Ej: Windows 11 23H2, Windows Server 2022"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Tamaño</Label>
                <Input
                  value={formData.tamano || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, tamano: e.target.value }))}
                  placeholder="Ej: 1.2 GB"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Descripción (opcional)</Label>
                <Textarea
                  value={formData.descripcion || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
                  placeholder="Descripción adicional..."
                  className="mt-1"
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter className="modal-footer">
              <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancelar</Button>
              <Button onClick={handleSave} disabled={saving} className="gap-2">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                Guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default KBCatalogPage;
